import { z } from "zod";
import { CURRENCIES } from "./money.js";
import { DEFAULT_CATEGORY, expenseCategorySchema } from "./category.js";
import { SPLIT_MODES } from "./split.js";

/**
 * Contratti HTTP delle spese.
 *
 * Gli importi viaggiano come **interi di centesimi**, mai come decimali: un
 * `12.34` in JSON è un float, e un float è esattamente ciò che questa app non
 * può permettersi. La conversione da «12,34» a `1234` avviene nel frontend con
 * `parseAmount`, prima di partire.
 */

const amountCentsSchema = z
  .number()
  .int("Gli importi sono centesimi interi")
  .max(Number.MAX_SAFE_INTEGER);

export const expenseDescriptionSchema = z
  .string()
  .trim()
  .min(1, "Serve una descrizione")
  .max(120, "Al massimo 120 caratteri");

export const payerInputSchema = z.object({
  memberId: z.string().min(1),
  amountCents: amountCentsSchema.positive("Un pagatore deve aver messo qualcosa"),
});

export const participantInputSchema = z.object({
  memberId: z.string().min(1),
  /** Quote per `shares`, percentuale per `percent`. */
  weight: z.number().nonnegative().optional(),
  /** Importo assegnato a mano. Solo per `exact`. */
  amountCents: amountCentsSchema.nonnegative().optional(),
});

export const createExpenseRequestSchema = z.object({
  description: expenseDescriptionSchema,
  amountCents: amountCentsSchema.positive("L'importo dev'essere maggiore di zero"),
  currency: z.enum(CURRENCIES),
  /** ISO 8601. Assente significa adesso. */
  spentAt: z.iso.datetime().optional(),
  splitMode: z.enum(SPLIT_MODES),
  /** Assente significa «Altro»: categorizzare resta facoltativo. */
  category: expenseCategorySchema.default(DEFAULT_CATEGORY),
  payers: z.array(payerInputSchema).min(1, "Serve almeno un pagatore"),
  participants: z.array(participantInputSchema).min(1, "Serve almeno una persona"),
});
export type CreateExpenseRequest = z.infer<typeof createExpenseRequestSchema>;

/**
 * La modifica sostituisce la spesa per intero.
 *
 * Non è una PATCH parziale di proposito: cambiare l'importo senza ricalcolare
 * le quote produrrebbe una spesa sbilanciata, e cambiare le quote senza sapere
 * il totale è impossibile. Le due cose viaggiano insieme o non viaggiano.
 */
export const updateExpenseRequestSchema = createExpenseRequestSchema;
export type UpdateExpenseRequest = z.infer<typeof updateExpenseRequestSchema>;

// --- Risposte ---------------------------------------------------------------

export const expensePayerResponseSchema = z.object({
  memberId: z.string(),
  displayName: z.string(),
  amountCents: z.number().int(),
});

export const expenseShareResponseSchema = z.object({
  memberId: z.string(),
  displayName: z.string(),
  amountCents: z.number().int(),
  /** Il parametro originale, per rimostrare la divisione com'era stata scritta. */
  weight: z.number().nullable(),
});

export const expenseResponseSchema = z.object({
  id: z.string(),
  groupId: z.string(),
  description: z.string(),
  amountCents: z.number().int(),
  currency: z.enum(CURRENCIES),
  spentAt: z.string(),
  splitMode: z.enum(SPLIT_MODES),
  category: expenseCategorySchema,
  createdBy: z.object({ memberId: z.string(), displayName: z.string() }),
  payers: z.array(expensePayerResponseSchema),
  shares: z.array(expenseShareResponseSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExpenseResponse = z.infer<typeof expenseResponseSchema>;

/** La riga della lista: non porta quote e pagatori, che servono solo al dettaglio. */
export const expenseSummaryResponseSchema = z.object({
  id: z.string(),
  description: z.string(),
  amountCents: z.number().int(),
  currency: z.enum(CURRENCIES),
  spentAt: z.string(),
  splitMode: z.enum(SPLIT_MODES),
  category: expenseCategorySchema,
  payerNames: z.array(z.string()),
  /** Quanto tocca a chi sta guardando. `null` se non partecipa alla spesa. */
  myShareCents: z.number().int().nullable(),
  participantCount: z.number().int().nonnegative(),
});
export type ExpenseSummaryResponse = z.infer<typeof expenseSummaryResponseSchema>;
